
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../../styles/hyper-necropolis.css";

const FinalTransmission = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  

  return (
    <div
      ref={ref}
      className={`final-transmission ${visible ? "active" : ""}`}
    >
      <span>CONNECTION TERMINATED.</span>
      <span>LAST SEEN: JUST NOW</span>
      <span className="final-cursor">_</span>
    </div>
  );
};

const parseFrontmatter = (markdown) => {
  const match = markdown.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);

  if (!match) {
    return { metadata: {}, content: markdown };
  }

  const metadata = match[1].split(/\r?\n/).reduce((values, line) => {
    const entry = line.match(/^([\w-]+):\s*(.*)$/);

    if (entry) {
      values[entry[1]] = entry[2]
        .replace(/^["']|["']$/g, "")
        .trim();
    }

    return values;
  }, {});

  return {
    metadata,
    content: markdown.slice(match[0].length),
  };
};

// ------------------------------------------------
// 1. TERMINAL SECTION HEADINGS
// ------------------------------------------------

const BootHeading = ({ children, level = 2 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState(0);

  const title = String(children);
  const HeadingTag = `h${level}`;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || phase >= 4) return;

    const timer = setTimeout(() => {
      setPhase((previous) => previous + 1);
    }, phase === 0 ? 200 : 350);

    return () => clearTimeout(timer);
  }, [visible, phase]);

  return (
    <div ref={ref} className="necropolis-section-heading">
      {visible && phase < 4 && (
        <div className="necropolis-boot" aria-hidden="true">
          <div>&gt; INITIALIZING SECTOR...</div>
          {phase >= 1 && <div>&gt; SCANNING ARCHIVE...</div>}
          {phase >= 2 && <div>&gt; MEMORY ACCESS GRANTED</div>}
          {phase >= 3 && <div>&gt; LOADING {title.toUpperCase()}_</div>}
        </div>
      )}

      {phase >= 4 && (
        <HeadingTag className="necropolis-heading-reveal">
          <span className="necropolis-heading-prefix">&gt; </span>
          {children}
          <span className="necropolis-cursor">_</span>
        </HeadingTag>
      )}
    </div>
  );
};

// ------------------------------------------------
// 3. DECAYING TEXT
// ------------------------------------------------

const CHARACTERS = "░▒▓#@$%&/\\01";

const corruptText = (text, amount) => {
  return text
    .split("")
    .map((character) => {
      if (character === " " || Math.random() > amount) {
        return character;
      }

      return CHARACTERS[
        Math.floor(Math.random() * CHARACTERS.length)
      ];
    })
    .join("");
};

const DecayingText = ({ children }) => {
  const original = String(children);
  const [display, setDisplay] = useState(original);
  const [abandoned, setAbandoned] = useState(false);

  useEffect(() => {
    if (!abandoned) {
      setDisplay(original);
      return;
    }

    let decay = 0;

    const interval = setInterval(() => {
      decay = Math.min(decay + 0.06, 0.85);
      setDisplay(corruptText(original, decay));

      if (decay >= 0.85) clearInterval(interval);
    }, 100);

    return () => clearInterval(interval);
  }, [abandoned, original]);

  return (
    <span
      className={`necropolis-decaying-text ${
        abandoned ? "is-decaying" : ""
      }`}
      onMouseEnter={() => setAbandoned(false)}
      onMouseLeave={() => setAbandoned(true)}
      onFocus={() => setAbandoned(false)}
      onBlur={() => setAbandoned(true)}
      tabIndex={0}
      aria-label={original}
    >
      <span aria-hidden="true">{display}</span>
    </span>
  );
};

// ------------------------------------------------
// MAIN PAGE
// ------------------------------------------------

const HyperNecropolis = () => {
  const [essay, setEssay] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const loadEssay = async () => {
      try {
        const baseUrl = process.env.PUBLIC_URL || "";

        const response = await fetch(
          `${baseUrl}/content/essays/HyperNecropolis.md`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        setEssay(parseFrontmatter(await response.text()));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Failed to load essay:", error);
          setLoadError("This essay could not be loaded.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadEssay();

    return () => controller.abort();
  }, []);

  useEffect(() => {
  if (!essay) return;

  const finalSentence =
    "Nothing truly disappears online and the internet does not bury the dead.";

  const paragraphs = document.querySelectorAll(
    ".hyper-necropolis__article-content p"
  );

  const paragraph = Array.from(paragraphs).find(
    (p) => p.textContent.trim() === finalSentence
  );

  if (!paragraph) return;

  paragraph.classList.add("necropolis-final-text");

  // Wrap each character in a span
  paragraph.innerHTML = [...finalSentence]
    .map(
      (char, index) =>
        `<span style="--delay:${0.5 + ((index * 17) % 37) / 10}s;
          --remaining:${0.12 + ((index * 13) % 9) / 30}">
          ${char === " " ? "&nbsp;" : char}
        </span>`
    )
    .join("");

  // Start animation when the sentence enters the viewport
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        paragraph.classList.add("visible");
        observer.disconnect();
      }
    },
    { threshold: 0.6 }
  );

  observer.observe(paragraph);

  return () => observer.disconnect();
}, [essay]);

  const markdownComponents = {
    h1: ({ children }) => (
      <BootHeading level={2}>{children}</BootHeading>
    ),

    h2: ({ children }) => (
      <BootHeading level={2}>{children}</BootHeading>
    ),

    p: ({ children }) => {
      return <p>{children}</p>;
    },

    strong: ({ children }) => (
      <DecayingText>{children}</DecayingText>
    ),
  };

  return (
    <main
      className="hyper-necropolis"
      style={{
        "--hyper-necropolis-banner": 'url("/images/banner.png")',
      }}
    >
      <div className="hyper-necropolis__inner">
        {loading ? (
          <p className="hyper-necropolis__status" role="status">
            Loading essay...
          </p>
        ) : loadError ? (
          <p className="hyper-necropolis__status" role="alert">
            {loadError}
          </p>
        ) : (
          <>
            <header className="hyper-necropolis__header">
              <p className="hyper-necropolis__eyebrow">
                {essay.metadata.eyebrow || "Essay"}
                {essay.metadata.date
                  ? ` / ${essay.metadata.date}`
                  : ""}
              </p>

              <h1>{essay.metadata.title || "Hyper Necropolis"}</h1>

              {essay.metadata.dek && (
                <div className="hyper-necropolis__dek">
                  <ReactMarkdown>{essay.metadata.dek}</ReactMarkdown>
                </div>
              )}
            </header>

            <article className="hyper-necropolis__article">
              <img
                className="hyper-necropolis__skulls"
                src="/images/skulls.jpg"
                alt=""
                aria-hidden="true"
              />

              <div className="map-video-bg">
                <video autoPlay muted loop playsInline>
                  <source src="/images/lain.mp4" type="video/mp4" />
                </video>
              </div>

              <div className="hyper-necropolis__article-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {essay.content}
                </ReactMarkdown>
              </div>
              <FinalTransmission />
            </article>
          </>
        )}
      </div>
    </main>
  );
};

export default HyperNecropolis;
